import { api } from 'restaurantAdmin/api';
import type { CancellationFeeAttributes } from 'restaurantAdmin/guestBook/apiHelpers';
import { GuestTag } from 'restaurantAdmin/restaurants/apiHelpers';
import {
  type LightspeedOrder,
  type ReservationGuest,
  type ReservationStatus,
  type TransactionDetails,
} from '@shared/types/reservations';
import type { WalkInStatus } from '@shared/types/walkIns';
import type { OccupantType } from '../apiHelpers';
import { MultiTableReservationData } from '../concierge/types';
import type { ServiceStatus } from './serviceStatus/ServiceStatus';

export interface Server {
  hexColor: string;
  id: string;
  name: string;
}

export interface Seating {
  id: string;
  createdAt: string;
  floorPlanTableId: string | null;
  reservationId?: string | null;
  walkInId?: string | null;
  lastServedById?: string | null;
}

export interface WalkInSeating extends Seating {
  walkInId: string;
}

export interface ReservationSeating extends Seating {
  reservationId: string;
}

export interface ServiceWalkIn {
  type: 'walkIn';
  date: string;
  firstName?: string;
  guestCount: number;
  id: string;
  lastName?: string;
  seatedFloorPlanId: string | null;
  seatedTableId: string | null;
  seatedTableName: string;
  seatings: WalkInSeating[];
  server: Server | null;
  serviceStatus: ServiceStatus;
  status: WalkInStatus;
  time: string;
  guest: ReservationGuest | null;
  lightspeedOrder?: LightspeedOrder | null;
}

export interface ReservationCounts {
  total: number;
  finished: number;
  upcoming: number;
  noShow: number;
  canceled: number;
}

export interface GuestCounts {
  total: number;
  seated: number;
  finished: number;
  upcoming: number;
  noShow: number;
  canceled: number;
}

type GuestCountsByServer = Record<
  string,
  {
    serverName: string;
    activeGuestCount: number;
    totalGuestCount: number;
  }
>;

export interface ServiceStats {
  reservationCounts: ReservationCounts;
  guestCounts: GuestCounts;
  guestCountsByServer: GuestCountsByServer;
}

export interface PurchasedRestaurantEventAddOn {
  id: string;
  name: string;
  quantity: number;
  reservationId: string;
  restaurantEventAddOnId: string;
}

/**
 * Hand-maintained duplicate of the API's `OperaHotelBlock`
 * (`api/src/restaurantAdmins/service/ServiceMap.ts`), mapped onto
 * `ServiceReservation.hotel`.
 */
export interface OperaHotelBlock {
  status: 'auto_linked' | 'confirmed' | 'suggested' | 'lapsed';
  isInHouse: boolean;
  roomNumber: string | null;
  vipCode: string | null;
  lastSyncedAt: string | null;
}

export interface ServiceReservation {
  type: 'reservation';
  date: string;
  guest: ReservationGuest;
  guestCount: number;
  duration: number | null;
  turnTime: number | null;
  hasDietaryRestrictionOrAllergy: boolean;
  hasGuestNote: boolean;
  hotel: OperaHotelBlock | null;
  id: string;
  isAdmitted: boolean;
  isAvailabilityAgnostic: boolean;
  cancellationPolicy: { fee: number } | null;
  isEligibleForCancellationFee: boolean;
  isMultiTable: boolean;
  isStranded: boolean;
  isVIP: boolean;
  lightspeedOrder?: LightspeedOrder | null;
  listingFloorPlanId: string;
  listingName: string;
  note: string | null;
  purchasedRestaurantEventAddOns: PurchasedRestaurantEventAddOn[];
  staffNote: string | null;
  seatedTableName: string | null;
  seatings: ReservationSeating[];
  server: Server | null;
  serviceStatus: ServiceStatus;
  status: ReservationStatus;
  systemNote: string | null;
  time: string;
  transactionDetails: TransactionDetails;
  cancellationFeeTransaction: CancellationFeeAttributes | null;
  tableAssignments: TableAssignment[];
  guestTags: GuestTag[];
  wasTableChosenAtTimeOfBooking: boolean;
  restaurantEventId: string | null;
  eventName: string | null;
  eventTierName: string | null;
}

export interface TableAssignment {
  listingId: string;
  floorPlanTables?: TableAssignmentFloorPlanTable[];
}

export interface TableAssignmentFloorPlanTable {
  id: string;
  name: string;
}

export type ServiceOccupant = ServiceReservation | ServiceWalkIn;

export const isReservation = (
  occupant: Pick<ServiceOccupant, 'type'> | undefined | null,
): occupant is ServiceReservation => occupant?.type === 'reservation';

export const isWalkIn = (
  occupant: Pick<ServiceOccupant, 'type'> | undefined | null,
): occupant is ServiceWalkIn => occupant?.type === 'walkIn';

export const hasEvent = (reservation: ServiceReservation): boolean =>
  !!reservation.eventName;

export const getOccupants = async ({
  restaurantId,
  startDate,
  startTime,
  endDate,
  endTime,
  guestSearchQuery,
  signal,
  omitStatuses,
}: {
  restaurantId: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  guestSearchQuery?: string;
  signal?: AbortSignal;
  omitStatuses?: ReservationStatus[];
}): Promise<ServiceOccupant[]> => {
  const baseParams = { startDate, startTime, endDate, endTime };
  const params = guestSearchQuery
    ? { ...baseParams, guestSearchQuery }
    : baseParams;

  const reservationStatuses: ReservationStatus[] = [
    'active',
    'autoResolved',
    'canceled',
    'finished',
    'noShow',
  ];

  const includedStatuses = omitStatuses
    ? reservationStatuses.filter((status) => !omitStatuses.includes(status))
    : reservationStatuses;
  const walkInStatuses: WalkInStatus[] = ['active', 'autoResolved', 'finished'];

  const searchParams = new URLSearchParams(params);

  includedStatuses.forEach((status) => {
    searchParams.append('reservationStatus', status);
  });
  walkInStatuses.forEach((status) => {
    searchParams.append('walkInStatus', status);
  });

  return api.get(
    `/restaurants/${restaurantId}/service/occupants?${searchParams}`,
    {
      signal,
    },
  );
};

export const getServiceReservationDetails = async (
  restaurantId: string,
  reservationId: string,
): Promise<ServiceReservation> =>
  api.get(`/restaurants/${restaurantId}/service/reservations/${reservationId}`);

export const getServiceWalkInDetails = async (
  restaurantId: string,
  walkInId: string,
): Promise<ServiceWalkIn> =>
  api.get(`/restaurants/${restaurantId}/service/walk-ins/${walkInId}`);

export interface ReservationActivity {
  id: string;
  name: string;
  change: string;
  date: string;
  time: string;
}

export const getReservationActivities = async (
  restaurantId: string,
  reservationId: string,
): Promise<ReservationActivity[]> =>
  api.get(
    `/restaurants/${restaurantId}/reservations/${reservationId}/activities`,
  );

export const getServiceStats = async (
  restaurantId: string,
  date: string,
): Promise<ServiceStats> =>
  api.get(`/restaurants/${restaurantId}/service-stats/${date}`);

export const createReservationServiceStatus = async (
  restaurantId: string,
  reservationId: string,
  serviceStatus: ServiceStatus,
): Promise<void> =>
  api.post(
    `/restaurants/${restaurantId}/service/reservations/${reservationId}/service-statuses`,
    { serviceStatus },
  );

export const createWalkInServiceStatus = async (
  restaurantId: string,
  walkInId: string,
  serviceStatus: ServiceStatus,
): Promise<void> =>
  api.post(
    `/restaurants/${restaurantId}/service/walk-ins/${walkInId}/service-statuses`,
    { serviceStatus },
  );

export const updateOccupantServer = async ({
  occupantId,
  occupantType,
  restaurantId,
  serverId,
}: {
  occupantId: string;
  occupantType: OccupantType;
  restaurantId: string;
  serverId: string | null;
}): Promise<void> => {
  const apiOccupantType = occupantType === 'walkIn' ? 'walk-in' : 'reservation';

  return api.put(`/restaurants/${restaurantId}/occupant/update-server`, {
    occupantId,
    occupantType: apiOccupantType,
    serverId,
  });
};

export const blockReservations = async ({
  restaurantId,
  startDate,
  endDate,
  firstReservationTime,
  lastReservationTime,
  guestFacingReason,
}: {
  restaurantId: string;
  startDate: string;
  endDate: string;
  firstReservationTime?: string | null;
  lastReservationTime?: string | null;
  guestFacingReason?: string;
}): Promise<void> =>
  api.post(`/v2/restaurants/${restaurantId}/closures`, {
    startDate,
    endDate,
    ...(firstReservationTime && { firstReservationTime }),
    ...(lastReservationTime && { lastReservationTime }),
    ...(guestFacingReason && { guestFacingReason }),
  });

export const unblockReservations = (
  restaurantId: string,
  closureId: string,
): Promise<void> =>
  api.delete(`/v2/restaurants/${restaurantId}/closures/${closureId}`);

export const updateReservation = async ({
  restaurantId,
  reservationId,
  date,
  guestCount,
  time,
  listingId,
  wasTableChosen,
}: {
  restaurantId: string;
  reservationId: string;
  date: string;
  guestCount: number;
  time: string;
  listingId: string;
  wasTableChosen: boolean;
}): Promise<void> =>
  api.put(`/restaurants/${restaurantId}/reservations/${reservationId}`, {
    date,
    guestCount,
    time,
    listingId,
    wasTableChosen,
  });

export const updateReservationGuestCount = async ({
  guestCount,
  reservationId,
  restaurantId,
}: {
  guestCount: number;
  reservationId: string;
  restaurantId: string;
}): Promise<void> =>
  api.post(
    `/restaurants/${restaurantId}/reservations/${reservationId}/update-guest-count`,
    {
      guestCount,
    },
  );

export const updateWalkInGuestCount = async ({
  guestCount,
  walkInId,
  restaurantId,
}: {
  guestCount: number;
  walkInId: string;
  restaurantId: string;
}): Promise<void> =>
  api.post(
    `/restaurants/${restaurantId}/walk-ins/${walkInId}/update-guest-count`,
    {
      guestCount,
    },
  );

type UpdateLargePartyReservationRequest = MultiTableReservationData & {
  restaurantId: string;
  reservationId: string;
};

export const updateLargePartyReservation = async ({
  restaurantId,
  reservationId,
  floorPlanTableIds,
  guestCount,
  duration,
  date,
  time,
}: UpdateLargePartyReservationRequest): Promise<void> => {
  return api.put(
    `/restaurants/${restaurantId}/reservations/large-party/${reservationId}`,
    {
      floorPlanTableIds,
      guestCount,
      duration,
      date,
      time,
    },
  );
};

export interface RestaurantDayNote {
  date: string;
  note: string;
  restaurantId: string;
}

export const getRestaurantNote = async ({
  restaurantId,
  date,
}: {
  restaurantId: string;
  date: string;
}): Promise<RestaurantDayNote | null> =>
  api.get(`/restaurants/${restaurantId}/day-notes/${date}`);

export const saveRestaurantNote = async ({
  date,
  note,
  restaurantId,
}: RestaurantDayNote): Promise<RestaurantDayNote> =>
  api.post(`/restaurants/${restaurantId}/day-notes/${date}`, {
    note,
  });

export const deleteRestaurantNote = async ({
  date,
  restaurantId,
}: {
  date: string;
  restaurantId: string;
}): Promise<void> =>
  api.delete(`/restaurants/${restaurantId}/day-notes/${date}`);

export const admitGuest = async ({
  restaurantId,
  reservationId,
}: {
  restaurantId: string;
  reservationId: string;
}): Promise<void> =>
  api.post(
    `/restaurants/${restaurantId}/reservations/${reservationId}/admit`,
    {},
  );

export const unadmitGuest = async ({
  restaurantId,
  reservationId,
}: {
  restaurantId: string;
  reservationId: string;
}): Promise<void> =>
  api.delete(
    `/restaurants/${restaurantId}/reservations/${reservationId}/admit`,
  );
